import { RaitingsEntity } from '@/entities/api/products/product'
import { useMemo, useState, useEffect, useRef } from 'react'
import RatingStars from './raithing-star'
import { API_URL } from '@/services/api/keys'

interface ProductRatingsSectionProps {
	ratings: RaitingsEntity[]
}

const ProductRatingsSection: React.FC<ProductRatingsSectionProps> = ({
	ratings,
}) => {
	const [sortBy, setSortBy] = useState<
		'newest' | 'oldest' | 'highest' | 'lowest'
	>('highest')
	const [filterRating, setFilterRating] = useState<number | null>()
	const [showAll, setShowAll] = useState(false)
	const [visibleCount, setVisibleCount] = useState(3)
	const [isLoadingMore, setIsLoadingMore] = useState(false)
	const scrollContainerRef = useRef<HTMLDivElement>(null)

	const averageRating = useMemo(() => {
		if (ratings.length === 0) return 0
		return (
			ratings.reduce((sum, rating) => sum + rating.rate, 0) / ratings.length
		)
	}, [ratings])

	const filteredAndSortedRatings = useMemo(() => {
		let filtered = filterRating
			? ratings.filter((rating) => Math.floor(rating.rate) === filterRating)
			: ratings

		return filtered.sort((a, b) => {
			switch (sortBy) {
				case 'newest':
					return (
						new Date(b._created!).getTime() - new Date(a._created!).getTime()
					)
				case 'oldest':
					return (
						new Date(a._created!).getTime() - new Date(b._created!).getTime()
					)
				case 'highest':
					return b.rate - a.rate
				case 'lowest':
					return a.rate - b.rate
				default:
					return 0
			}
		})
	}, [ratings, sortBy, filterRating])

	const displayedRatings = useMemo(() => {
		if (!showAll) {
			return filteredAndSortedRatings.slice(0, 3)
		}
		return filteredAndSortedRatings.slice(0, visibleCount)
	}, [filteredAndSortedRatings, showAll, visibleCount])

	const hasMoreRatings = filteredAndSortedRatings.length > 3

	const loadMoreRatings = () => {
		setIsLoadingMore(true)
		setTimeout(() => {
			setVisibleCount((prev) =>
				Math.min(prev + 5, filteredAndSortedRatings.length)
			)
			setIsLoadingMore(false)
		}, 300)
	}

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
		const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100

		if (
			isNearBottom &&
			!isLoadingMore &&
			visibleCount < filteredAndSortedRatings.length
		) {
			loadMoreRatings()
		}
	}

	useEffect(() => {
		if (!showAll) {
			setVisibleCount(3)
		}
	}, [showAll, sortBy, filterRating])

	return (
		<div className='bg-white border border-gray-200 rounded-lg overflow-hidden'>
			<div className='border-b border-gray-100 px-6 py-5'>
				<div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6'>
					<div className='space-y-3'>
						<h3 className='text-xl lg:text-2xl font-semibold text-gray-900'>
							Reseñas de clientes
						</h3>
						<div className='flex items-center gap-4'>
							<RatingStars rating={averageRating} size='lg' />
							<span className='text-xl font-semibold text-gray-900'>
								{averageRating.toFixed(1)}
							</span>
							<span className='text-sm text-gray-600 font-medium'>
								({ratings.length} reseña{ratings.length !== 1 ? 's' : ''})
							</span>
						</div>
					</div>

					<div className='flex flex-col sm:flex-row gap-3'>
						<select
							value={sortBy}
							onChange={(e) => setSortBy(e.target.value as any)}
							className='bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm font-medium text-gray-900 focus:ring-1 focus:ring-red-500 focus:border-red-500 transition-colors'
						>
							<option value='newest'>Más recientes</option>
							<option value='oldest'>Más antiguas</option>
							<option value='highest'>Mayor puntuación</option>
							<option value='lowest'>Menor puntuación</option>
						</select>
					</div>
				</div>
			</div>

			<div className='relative'>
				<div
					ref={scrollContainerRef}
					className={`px-6 py-6 space-y-6 ${
						showAll ? 'max-h-96 overflow-y-auto' : ''
					}`}
					onScroll={showAll ? handleScroll : undefined}
				>
					{displayedRatings.map((rating) => (
						<div
							key={rating._id}
							className='border-b border-gray-100 last:border-b-0 pb-6 last:pb-0'
						>
							<div className='flex items-start gap-4'>
								<div className='flex-shrink-0'>
									{rating.customer.images &&
									rating.customer.images.length > 0 ? (
										<img
											src={`${API_URL}/${rating.customer.images[0].url}`}
											alt={`${rating.customer.name}`}
											className='w-12 h-12 rounded-full object-cover border border-gray-200'
										/>
									) : (
										<div className='w-12 h-12 rounded-full bg-red-600 flex items-center justify-center border border-gray-200'>
											<span className='text-white font-semibold text-lg'>
												{rating.customer.name?.charAt(0).toUpperCase()}
											</span>
										</div>
									)}
								</div>

								<div className='flex-1 min-w-0'>
									<div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3'>
										<div>
											<h4 className='font-semibold text-gray-900 text-base'>
												{rating.customer.name} {rating.customer.lastname}
											</h4>
											<div className='flex items-center gap-3 mt-1'>
												<RatingStars rating={rating.rate} size='sm' />
												<span className='text-sm font-medium text-gray-700'>
													{rating.rate.toFixed(1)}
												</span>
											</div>
										</div>
										<span className='text-sm text-gray-500'>
											{new Date(rating._created!).toLocaleDateString('es-ES', {
												year: 'numeric',
												month: 'long',
												day: 'numeric',
											})}
										</span>
									</div>

									<p className='text-gray-700 text-base leading-relaxed'>
										{rating.comment}
									</p>

									{rating.rate >= 4.5 && (
										<div className='mt-3'>
											<span className='inline-flex items-center px-2.5 py-1 rounded-md bg-green-50 text-green-700 text-xs font-medium border border-green-200'>
												Reseña destacada
											</span>
										</div>
									)}
								</div>
							</div>
						</div>
					))}

					{isLoadingMore && (
						<div className='flex justify-center py-4'>
							<div className='animate-spin rounded-full h-6 w-6 border-2 border-red-600 border-t-transparent'></div>
						</div>
					)}
				</div>

				{!showAll && hasMoreRatings && (
					<div className='relative'>
						<div className='absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none'></div>
						<div className='px-6 pb-6 pt-2 flex justify-center relative z-10'>
							<button
								onClick={() => setShowAll(true)}
								className='inline-flex cursor-pointer items-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
							>
								Ver todas las reseñas ({filteredAndSortedRatings.length})
								<svg
									className='ml-2 w-4 h-4'
									fill='none'
									stroke='currentColor'
									viewBox='0 0 24 24'
								>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M19 9l-7 7-7-7'
									/>
								</svg>
							</button>
						</div>
					</div>
				)}

				{showAll && (
					<div className='px-6 pb-6 flex justify-center border-t border-gray-100 pt-4'>
						<button
							onClick={() => setShowAll(false)}
							className='inline-flex items-center px-4 py-2 text-gray-600 hover:text-red-600 font-medium text-sm transition-colors focus:outline-none'
						>
							<svg
								className='mr-2 w-4 h-4'
								fill='none'
								stroke='currentColor'
								viewBox='0 0 24 24'
							>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M5 15l7-7 7 7'
								/>
							</svg>
							Mostrar menos
						</button>
					</div>
				)}
			</div>

			{filteredAndSortedRatings.length === 0 && (
				<div className='px-6 py-12 text-center'>
					<div className='w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full border border-gray-300'>
						<svg
							className='w-6 h-6 text-gray-400'
							fill='none'
							stroke='currentColor'
							viewBox='0 0 24 24'
						>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={1.5}
								d='M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.562M15 6.306a7.962 7.962 0 00-6 0m6 0V3a1 1 0 00-1-1H10a1 1 0 00-1 1v3.306M15 6.306V9a1 1 0 01-1 1H10a1 1 0 01-1-1V6.306'
							/>
						</svg>
					</div>
					<h4 className='text-lg font-semibold text-gray-900 mb-2'>
						No hay reseñas que coincidan
					</h4>
					<p className='text-gray-600 text-sm max-w-sm mx-auto mb-4'>
						Prueba ajustando los filtros para ver más resultados o explora otras
						valoraciones.
					</p>
					{filterRating && (
						<button
							onClick={() => setFilterRating(null)}
							className='inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
						>
							Limpiar filtros
						</button>
					)}
				</div>
			)}
		</div>
	)
}

export default ProductRatingsSection
